import { useEffect, useState } from 'react';

/** Tailwind's `sm` breakpoint, as the platform can be asked about it. */
const SM_UP_QUERY = '(min-width: 640px)';

/**
 * Whether the viewport is at or above Tailwind's `sm` breakpoint (640px)
 * (spec 140 FR-035b).
 *
 * It lives in `shared/hooks` rather than beside either caller because
 * `TaskCommentsPanel` and `TaskResultsPanel` ask the same question to decide
 * the same thing — whether a file link opens the companion panel or falls back
 * to the Files route — and two `matchMedia` calls would be two answers that can
 * drift. One hook keeps them a single answer. It is not a seam and takes no
 * configuration: one implementation, several consumers, which is a shared
 * utility rather than the abstraction rule 2.4 forbids.
 *
 * Live, not sampled. A reader who resizes the window — or rotates a tablet —
 * gets the destination their current width warrants on the next interaction
 * rather than at the next reload.
 *
 * The shape deliberately matches `useReducedMotion`: read once, subscribe,
 * clean up.
 *
 * `window.matchMedia` is treated as possibly absent throughout: without it the
 * hook answers `false` — the narrow, Files-route fallback — and subscribes to
 * nothing, rather than throwing.
 */
export function useSmUp(): boolean {
  /*
    `matchMedia` may be absent, and *both* paths treat it that way — the
    contract is one, not one per path (spec 140 FR-073).

    jsdom implements no `matchMedia`; the test setup stubs it, but a consumer
    rendered before that stub, or any environment without it, would see
    `undefined`. When it is absent the hook answers `false` — "narrow", the
    Files-route fallback — and subscribes to nothing. It never throws.

    The initial read used to be optional-chained while the effect called
    `window.matchMedia` outright, so the comment promised a guard the effect
    did not have. Whichever path a maintainer reads must describe the same
    hook, which is why the guard below is not optional.
  */
  const [smUp, setSmUp] = useState(
    () => window.matchMedia?.(SM_UP_QUERY).matches ?? false,
  );

  useEffect(() => {
    // The same absence the initial read tolerates: no query to read, nothing to
    // subscribe to, and `smUp` stays at the `false` the initializer chose.
    if (typeof window.matchMedia !== 'function') return;

    const query = window.matchMedia(SM_UP_QUERY);
    const sync = (event: MediaQueryList | MediaQueryListEvent) => {
      setSmUp(event.matches);
    };
    sync(query);
    query.addEventListener('change', sync);
    return () => {
      query.removeEventListener('change', sync);
    };
  }, []);

  return smUp;
}
